import { forwardRef, ReactNode, useEffect, useMemo } from 'react';

import { EntityRefUtil, GetCurrentWebUserResponseDTO } from '@gearbelt/global';
import { buttonVariants } from '@gearbelt/ui/components';
import { GearbeltPlanType } from '@site-mate/sitemate-global-shared/lib';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import dashpivotLogo from '@/assets/dashpivot-small.svg';
import flowsiteLogo from '@/assets/flowsite-small.svg';
import gearbeltLogo from '@/assets/gearbelt-small.svg';
import stormLogo from '@/assets/storm-small.svg';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, initials } from '@/lib/utils';
import { getSitemateStartAccountQueryOptions } from '@/queries/workspace-switcher';
import { useSMSAccountStore } from '@/stores/sms-account.store';
import { useUserStore } from '@/stores/user.store';
import { getWorkspaceRoute } from '@/utils/routes';

import GiftIcon from '../icons/gift';
import InformationIcon from '../icons/information';
import LogoutIcon from '../icons/logout';
import QuestionIcon from '../icons/question';
import UserIcon from '../icons/user';
import VideoIcon from '../icons/video';
import WalletIcon from '../icons/wallet';
import { useTopbarStore } from './topbar-store';

export function Topbar() {
  const { actions } = useTopbarStore();
  return (
    <div className="rounded-tl-10 border-grey-3 relative mt-2 flex h-[48px] w-full justify-between border-b-[1px] bg-white shadow-[0_4px_24px_#0000001a]">
      <div className="px-1">{actions}</div>
      <div className="m-1 flex flex-row items-center gap-0">
        <HelpMenu />
        <UserMenu />
      </div>
    </div>
  );
}

export function TopbarTab({ href, label, icon, active }: { href: string; label: string; icon?: ReactNode; active?: boolean }) {
  const location = useLocation();
  return (
    <Link
      to={href}
      className={cn(
        'text-secondary-text hover:text-default-text flex h-full items-center gap-2 border-y-[4px] border-transparent px-2 py-2 text-sm',
        (location.pathname === href || active) && 'border-b-default-blue text-default-text',
      )}>
      {!!icon && icon}
      <span>{label}</span>
    </Link>
  );
}

export function UserMenu() {
  const { user, setUser, setOrg } = useUserStore();
  const { signoutRedirect } = useAuth();

  if (!user) {
    return <></>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <UserMenuTrigger user={user} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
          <Link to={getWorkspaceRoute('/profile')}>
            <UserIcon />
            Edit Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="focus:bg-grey-1 flex cursor-pointer items-center gap-1.25"
          onClick={async () => {
            setUser(null);
            setOrg(null);
            await signoutRedirect({
              post_logout_redirect_uri: window.location.origin,
            });
          }}>
          <LogoutIcon />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const UserMenuTrigger = forwardRef<HTMLDivElement, { user: GetCurrentWebUserResponseDTO & { avatarUrl?: string } }>(({ user }, ref) => {
  return (
    <div ref={ref} className={cn(buttonVariants({ variant: 'ghost' }), 'flex h-10 items-center gap-1 p-2 focus-visible:outline-none')}>
      <Avatar className="h-[28px] w-[28px] rounded-[14px]">
        {user.avatarUrl ? (
          <AvatarImage className="aspect-square rounded-full border-2 border-white object-cover" src={user.avatarUrl} />
        ) : (
          <AvatarFallback className="size-[28px] border-2 border-white text-xs leading-3 font-bold">
            {initials(EntityRefUtil.createUserFullNameRef(user))}
          </AvatarFallback>
        )}
      </Avatar>
      <div className="flex flex-col items-end">
        <div className="text-md font-medium">{user.firstName}</div>
      </div>
    </div>
  );
});
UserMenuTrigger.displayName = 'UserMenuTrigger';

export function HelpMenu() {
  const { org } = useUserStore();
  const { setSMSAccount } = useSMSAccountStore();

  const { data: account } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));
  const isFreeTrialPlan = useMemo(() => (account?.billing?.gearbelt?.planType ?? GearbeltPlanType.FreeTrial) === GearbeltPlanType.FreeTrial, [account]);

  useEffect(() => {
    if (account) {
      setSMSAccount(account);
    }
  }, [account, setSMSAccount]);

  if (!org) {
    return <></>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <div className={cn(buttonVariants({ variant: 'ghost' }), 'flex items-center justify-center gap-1 p-2 text-sm font-medium')}>
          <QuestionIcon />
          Help
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
          <Link target="_blank" rel="noopener noreferrer" to="https://help.sitemate.com/en/collections/10502740-gearbelt">
            <VideoIcon />
            Watch video tutorials
          </Link>
        </DropdownMenuItem>

        {isFreeTrialPlan ? (
          <>
            <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
              <Link target="_blank" rel="noopener noreferrer" to="https://help.sitemate.com/en/articles/9924308-gearbelt-trial-information">
                <InformationIcon />
                Trial information
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
              <Link target="_blank" rel="noopener noreferrer" to="https://sitemate.com/software/gearbelt/pricing/">
                <WalletIcon />
                Pricing information
              </Link>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
              <Link target="_blank" rel="noopener noreferrer" to="https://help.sitemate.com/en/collections/2074918-release-notes">
                <GiftIcon />
                New
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="focus:bg-grey-1 flex items-center gap-1.25">
              <Link target="_blank" rel="noopener noreferrer" to="https://portal.productboard.com/jvll6vwezbekuksrtbxkugpq/tabs/14-in-progress">
                <img src={gearbeltLogo} alt="Gearbelt Roadmap" className="size-4" />
                Gearbelt roadmap
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-secondary-text text-xs font-medium">Other Sitemate Products</DropdownMenuLabel>

            <DropdownMenuItem asChild className="focus:bg-grey-1 flex flex-col items-start gap-1">
              <Link target="_blank" rel="noopener noreferrer" to="https://portal.productboard.com/7dz5kpm4pgr8vh81zbrhxez7/tabs/27-next">
                <div className="flex items-center gap-1.25">
                  <img src={stormLogo} alt="Storm" className="size-4" />
                  <div>Storm roadmap</div>
                </div>
                <div className="text-secondary-text text-xs leading-normal">
                  Front line AI agent for the built world - use voice, photos, notes and instructions to generate templates and fill out forms.
                </div>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="focus:bg-grey-1 flex flex-col items-start gap-1">
              <Link target="_blank" rel="noopener noreferrer" to="https://portal.productboard.com/wm6whau7st9md1ulztigrv4n/tabs/24-in-progress-">
                <div className="flex items-center gap-1.25">
                  <img src={dashpivotLogo} alt="Dashpivot" className="size-4" />
                  <div>Dashpivot roadmap</div>
                </div>
                <div className="text-secondary-text text-xs leading-normal">
                  Data capture and information management platform which powers hundreds of customisable systems and apps.
                </div>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="focus:bg-grey-1 flex flex-col items-start gap-1">
              <Link target="_blank" rel="noopener noreferrer" to="https://portal.productboard.com/m1f7fklok6htipfa26htialt/tabs/17-in-progress">
                <div className="flex items-center gap-1.25">
                  <img src={flowsiteLogo} alt="Flowsite" className="size-4" />
                  <div>Flowsite roadmap</div>
                </div>
                <div className="text-secondary-text text-xs leading-normal">
                  Integrations platform for automating business processes and eliminating data entry with instant no-code integrations.
                </div>
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
